import React, {useContext, useRef} from "react";
import * as Phase2State from './state';
import {Alert, Button, Col, Divider, Dropdown, Icon, Input, Layout, Menu, message, Modal, Row, Tooltip} from "antd";
import {BASE_CLS, deRefLocations} from "./util";
import SelectionCount from "../../../../components/search/SelectionCount";
import GroupForm from "./form";
import {TransitType} from "../../../../common/types/carriable";
import './phase-2-scheduler.less';
import Phase2Table from "./table";
import useUserGroups from "../../../../hooks/useUserGroups";
import NewLegForm from "./new-leg-form";
import {useApolloClient} from "react-apollo";
import {Location} from "common/types/location";
import {NewLegFormData} from "./types";
import * as NotesForPilot from "../../../../components/flights/scheduling/notes-for-pilot";
import {getSNodeID} from "../../../../components/scheduling/util";
import {ScheduleNode} from "../../../../components/scheduling/types";
import PurgeEntitiesFromFlightTool from "./purge-entities-from-flight-tool";
import Toolbar from "./toolbar";
import ScheduledToExpectedPaxCgoTool from "./scheduled-to-expected-paxcgo";
import PaxNotifierDrawer, {usePaxNotifierDrawerState} from "../../../../components/editor/pax-notifier/drawer/drawer";
import CheckinPaxNotifier from "../../../../components/editor/pax-notifier/modes/check-in/checkin-pax-notifier";
import SafetyCardAlert from "../../../../components/scheduling/safety-card-alert";

const { Content } = Layout;

export interface Phase2EditorProps {
    groupFormRef: React.MutableRefObject<any>
}

const Phase2Editor: React.FC<Phase2EditorProps> = (props) => {

    // Context API -----------------------------------------------------------------------------------------------------
    const { api } = useContext(Phase2State.Context);

    const editMode = api.editMode.get();
    const saveStatus = api.saveStatus;
    // -----------------------------------------------------------------------------------------------------------------

    // Hooks -----------------------------------------------------------------------------------------------------------
    const groupFormRef = React.useRef(null);
    const newLegModalFormRef = React.useRef(null);
    const apolloClient = useApolloClient();
    const popupContainerRef = useRef<HTMLDivElement>(null);
    // -----------------------------------------------------------------------------------------------------------------

    // State helpers ---------------------------------------------------------------------------------------------------
    const scheduledPax = api.data.modifiedEntities.get().filter(c => c.classType === 'flytsuite.paxnode');
    const outboundPax = scheduledPax.filter(p => p.transitType === TransitType.OUTBOUND);
    // -----------------------------------------------------------------------------------------------------------------

    function runComplianceCheck() {
        if (api.editMode.get()){
            message.error('Cannot run compliance check in edit mode');
            return;
        }
        api.bulkComplianceChecker.begin(
            scheduledPax
                .filter(p => p.transitType === TransitType.OUTBOUND)
                .map((pax) => getSNodeID(api.toScheduleNode(pax)))
        );
    }

    // Form ------------------------------------------------------------------------------------------------------------
    const form = <GroupForm
        wrappedComponentRef={(ref) => {
            groupFormRef.current = ref;
            if (props.groupFormRef){
                props.groupFormRef.current = ref;
            }
        }}
        disabled={!editMode}
        formData={api.forms.groupForm.get()}
        onFieldsChange={api.forms.groupForm.setFields}
    />
    // -----------------------------------------------------------------------------------------------------------------

    // Table header ----------------------------------------------------------------------------------------------------
    const tableHeader = (
        <Row type="flex" className={BASE_CLS + '-table-header'}>
            <Col style={{ flex: 1 }}>
                {form}
            </Col>
            {groupFormRef.current && groupFormRef.current.props.form.isFieldsTouched() ? (
                <Col style={{ paddingTop: '0.2rem', paddingRight: '0.2rem' }}>
                    <Tooltip title="Reset fields" placement="topLeft">
                        <Button icon="undo" onClick={api.forms.groupForm.resetFields} />
                    </Tooltip>
                </Col>
            ) : null}
        </Row>
    )
    // -----------------------------------------------------------------------------------------------------------------

    // Leg tables ------------------------------------------------------------------------------------------------------
    // -----------------------------------------------------------------------------------------------------------------

    // Save status alert -----------------------------------------------------------------------------------------------
    let saveStatusAlert = null;
    if (saveStatus.get()){
        let content = null;
        if (saveStatus.get().status === 'success'){
            content = <>
                <Button
                    type="primary"
                    size="large"
                    onClick={api.schedule.finish}
                >Finish</Button>
                <Divider type="vertical" />
                <Button
                    onClick={() => {
                        runComplianceCheck();
                    }}
                    type="link"
                    size="large"
                >Run Compliance Check</Button>
            </>
        }
        const desc = (
            <>
                {saveStatus.get().description ? <p>{saveStatus.get().description}</p> : null}
                {content}
            </>
        )

        saveStatusAlert = (
            <Alert
                type={saveStatus.get().status === 'success' ? 'success' : 'error'}
                showIcon
                message={saveStatus.get().message}
                description={desc}
                closable
                onClose={api.saveStatus.dismiss}
            />
        )
    }
    // -----------------------------------------------------------------------------------------------------------------

    return <Content
            className={BASE_CLS + '-content'}
        >
        <div
            ref={popupContainerRef}
        >
            <Toolbar api={api} />
            {saveStatusAlert}
            <SafetyCardAlert passengers={scheduledPax} />
            {tableHeader}
            <Phase2Table
                containerElement={popupContainerRef?.current}
                api={api}
                ref={api.forms.entityForm.ref}
            />
            <Modal
                title="Move to New Leg"
                visible={api.modal.newLegFormModal.get().visible}
                onCancel={api.modal.newLegFormModal.close}
                okText='Move'
                onOk={() => {
                    newLegModalFormRef.current?.validateFieldsAndScroll(async (err, values: NewLegFormData) => {
                        if (err) return;

                        const departureID = values.depDest.departure?.key;
                        const destinationID = values.depDest.destination?.key;

                        // Convert the location IDs to real locations
                        const result = await deRefLocations(apolloClient, [departureID, destinationID]);
                        if (result.errors.length){
                            message.error('Failed to get locations');
                            return;
                        }
                        const [ departure, destination ] = result.data;

                        // Call the callback and close the modal (which clears the callback too)
                        api.modal.newLegFormModal.callback?.(departure, destination);
                        api.modal.newLegFormModal.close();
                    })
                }}
            >
                <NewLegForm
                    formData={api.forms.newLegForm.get()}
                    onFieldsChange={api.forms.newLegForm.setFields}
                    ref={newLegModalFormRef}
                />
            </Modal>
            <Modal
                title="Edit Notes For Pilot"
                visible={api.notesForPilot.editor.isOpen()}
                onCancel={api.notesForPilot.editor.close}
                bodyStyle={{ padding: 0 }}
                width={700}
                onOk={api.notesForPilot.editor.submit}
                okText="Apply"
            >
                <div
                    className={BASE_CLS + '-toolbar'}
                    style={{
                        marginBottom: '12'
                    }}
                >
                    <NotesForPilot.Editor.Toolbar
                        {...api.notesForPilot.editor.getToolbarProps()}
                    />
                </div>
                <Layout style={{
                    padding: 12,
                    maxHeight: 500,
                    height: 500,
                    overflow: 'auto'
                }}>
                    <NotesForPilot.Editor.Editor
                        {...api.notesForPilot.editor.getEditorProps()}
                        entities={api.data.modifiedEntities.getNotOnCarrier() as ScheduleNode[]}
                    />
                </Layout>
            </Modal>
        </div>
        <PurgeEntitiesFromFlightTool
            state={api.getState()}
            api={api}
        />
        <ScheduledToExpectedPaxCgoTool
            state={api.getState()}
            api={api}
        />
        <PaxNotifierDrawer {...api.paxNotifierDrawer}>
            <CheckinPaxNotifier
                paxData={outboundPax}
                scheduledFlightDate={api.forms.groupForm.get()?.date?.value}
                refetchAfterSave={async () => {
                    const result = await api.data.refetch();
                    return result?.data?.GetScheduledPaxCgoV2?.paxnodes;
                }}
            />
        </PaxNotifierDrawer>
    </Content>
}

export default Phase2Editor
